import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import { fetchData } from '../api/publicService';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PlaceIcon from '@mui/icons-material/Place';

import CtaBackgroundImage from '../assets/images/best1.png';
import Rating from '../components/Rating';

// --- STYLED COMPONENTS & PALETTE ---

const PALETTE = {
  stoneWhite: '#FCF9F3',
  peacockTeal: '#005B5C',
  saffronOrange: '#FCA510',
  textPrimary: '#1A2027',
  textSecondary: '#5F5F5F',
};

const JourneyCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px',
  boxShadow: '0 10px 30px rgba(0,0,0,0.07)',
  transition: 'transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    '& .destination-overlay': {
      transform: 'translateY(0)',
      opacity: 1,
    },
  },
}));

const DestinationOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '220px',
  padding: theme.spacing(2),
  color: 'white',
  background: 'linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.2) 100%)',
  backdropFilter: 'blur(3px)',
  transform: 'translateY(100%)',
  opacity: 0,
  transition: 'transform 0.4s ease-in-out, opacity 0.4s ease-in-out',
  overflowY: 'auto',
}));

const getCurrencySymbol = (currencyCode) => {
  switch (currencyCode.toLowerCase()) {
    case 'eur': return '€';
    case 'gbp': return '£';
    case 'usd':
    default: return '$';
  }
};

const Trips = () => {
  const { t } = useTranslation();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { language } = useLanguage();
  const { currency } = useCurrency();

  useEffect(() => {
    const loadTrips = async () => {
      try {
        setLoading(true);
        const data = await fetchData('/trips', language);
        setTrips(data);
        setError(null);
      } catch (err) {
        setError(t('trips_fetch_error'));
      } finally {
        setLoading(false);
      }
    };
    loadTrips();
  }, [language, t]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>;
  if (error) return <Container sx={{ py: 8 }}><Alert severity="error">{error}</Alert></Container>;

  return (
    <Box component="section" sx={{ backgroundColor: PALETTE.stoneWhite }}>
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography variant="h3" component="h2" sx={{ fontWeight: 500, mb: 3, lineHeight: 1.3, fontFamily: 'serif', color: '#005B4B' }}>
            {t('trips_main_headline')}
          </Typography>
          <Typography variant="h6" component="p" sx={{ color: PALETTE.textSecondary, maxWidth: '650px', mx: 'auto', fontWeight: 400 }}>
            {t('trips_main_subheadline')}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {trips.map((trip) => {
            const uniqueDestinations = [...new Set(
              trip.itinerary
                .map(day => day.destination?.name)
                .filter(Boolean)
            )];
            
            const price = trip.basePrice?.[currency] || trip.basePrice?.usd || 0;
            const symbol = getCurrencySymbol(currency);

            return (
              <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={trip._id}>
                <JourneyCard>
                  <Box sx={{ position: 'relative', height: '220px' }}>
                    <CardMedia
                      component="img"
                      height="220"
                      image={trip.images[0]}
                      alt={trip.title}
                    />
                    <DestinationOverlay className="destination-overlay">
                      <Typography variant="h6" fontWeight="bold" gutterBottom>{t('trips_card_overlay_title')}</Typography>
                      <List dense disablePadding>
                        {uniqueDestinations.slice(0, 4).map((name, index) => (
                          <ListItem key={index} disablePadding>
                            <ListItemIcon sx={{ minWidth: '32px' }}><PlaceIcon sx={{ color: 'white', fontSize: '1rem' }} /></ListItemIcon>
                            <ListItemText primary={name} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                          </ListItem>
                        ))}
                      </List>
                    </DestinationOverlay>
                  </Box>
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography variant="body2" color="primary" fontWeight="bold">
                      {t('trips_card_duration', { duration: trip.duration })} • {trip.tripType?.name}
                    </Typography>
                    
                    <Typography gutterBottom variant="h5" component="h3" sx={{ fontWeight: 600, color: '#005F69', mt: 1, mb: 2 }}>
                      {trip.title}
                    </Typography>

                    {/* --- FIXED: Added fallback '|| "Person"' --- */}
                    <Typography variant="h6" fontWeight="bold" sx={{ mt: 2, color: PALETTE.saffronOrange }}>
                        {t('trips_card_price', { 
                          symbol: symbol, 
                          price: price.toLocaleString(),
                          unit: trip.pricePer || 'Person' 
                        })}
                    </Typography>

                    <Typography variant="body2" color={PALETTE.textSecondary} sx={{
                        display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 3,
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        mt: 1
                      }}>
                      {trip.summary}
                    </Typography>
                     <Rating value={trip.totalRatings} text={`(${trip.ratings.length})`} />
                  </CardContent>
                  <CardActions sx={{ px: 3, pb: 3, mt: 'auto' }}>
                    <Button
                      component={Link}
                      to={`/trips/${trip.slug}`}
                      variant="contained"
                      fullWidth
                      size="large"
                      sx={{
                        backgroundColor: PALETTE.peacockTeal,
                        fontWeight: 600,
                        py: 1.5,
                        '&:hover': { backgroundColor: '#004243' },
                      }}
                    >
                      {t('trips_card_button')}
                    </Button>
                  </CardActions>
                </JourneyCard>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${CtaBackgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" component="h3" sx={{ fontWeight: 700, color: 'white', mb: 2 }}>
              {t('trips_cta_headline')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.85)', maxWidth: '600px', mx: 'auto', mb: 4 }}>
              {t('trips_cta_subheadline')}
            </Typography>
            <Button component={Link} to="/trip-planner" variant="contained" size="large" sx={{ backgroundColor: PALETTE.saffronOrange, color: PALETTE.textPrimary, fontWeight: 700, fontSize: '1rem', py: 2, px: 6, borderRadius: '8px', '&:hover': { backgroundColor: '#e69500' } }}>
              {t('trips_cta_button')}
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Trips;